<template>
  <div id="connection">
    <loading-indicator :loading="loading"></loading-indicator>
    <div class="row" v-if="!loading">
      <div class="col-lg-4">
        <h4>Connection Information</h4>
        <table class="table table-borderless table-sm">
          <tbody>
            <tr>
              <td class="indent has-text-right"><b>IP</b></td>
              <td>{{ connection.ip }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Serial</b></td>
              <td>{{ connection.serial }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Model</b></td>
              <td>{{ connection.model }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Sending Facility</b></td>
              <td>{{ connection.sendingFacility }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Established</b></td>
              <td>{{ connection.established | date }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div id="connection-location" class="col-lg-4">
        <h4>Location</h4>
        <table class="table table-borderless table-sm">
          <tbody>
            <tr>
              <td class="indent has-text-right"><b>Facility</b></td>
              <td>{{ location.facility }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right text-nowrap">
                <b>Point Of Care</b>
              </td>
              <td>{{ location.pointOfCare }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Building</b></td>
              <td>{{ location.building }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Floor</b></td>
              <td>{{ location.floor }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Room</b></td>
              <td>{{ location.room }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right"><b>Bed</b></td>
              <td>{{ location.bed }}</td>
            </tr>
            <tr>
              <td class="indent has-text-right text-nowrap">
                <b>Full Bed Name</b>
              </td>
              <td>{{ location.fullBedName }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div id="connection-patient" class="col-lg-4">
        <h4>Patient</h4>
        <table class="table table-borderless table-sm">
          <tbody>
            <tr>
              <td class="indent has-text-right"><b>ID</b></td>
              <td>{{ patient.ID }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <h4>
          Data Stream
          <span class="last-message">(last message: <elapsed :time="receivedTime" />)</span>
        </h4>
        <pre class="live-viewer">{{ messagesString }}</pre>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <h4>
          Last State
          <span class="text-muted font-weight-normal" style="font-size: 16px">
            {{ displayedItems.length }} parameter(s)
          </span>
        </h4>
        <p class="control is-expanded">
          <input id="connection-filter" v-model="search" class="form-control" placeholder="Filter by ID" />
        </p>
        <div id="aggregation-table" class="columns is-gapless is-multiline">
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th>Class</th>
                <th>ID</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in displayedItems" :key="s.label">
                <td>{{ s.type }}</td>
                <td>{{ s.label }}</td>
                <td>
                  <template v-if="s.type !== 'scalar'">
                    <b>{{ s.value }}</b>
                  </template>
                  <template v-else>
                    <wave-chart class="fit-canvas" :data="s.value"></wave-chart>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.indent {
  padding-right: 20px;
}

.last-message {
  font-size: small;
  font-style: italic;
}
</style>

<script>
import CoreAPI from "@/utils/core-api";
import * as modals from "@/utils/modals";
import WaveChart from "@/components/connection/WaveChart";
import LoadingIndicator from "@/components/LoadingIndicator";
import Elapsed from "@/components/Elapsed";

export default {
  name: "Connection",
  components: {
    WaveChart,
    LoadingIndicator,
    Elapsed
  },
  data() {
    return {
      loading: true,
      search: "",
      connection: {},
      location: {},
      patient: {},
      messages: [],
      params: {},
      wavedata: {},
      ws: null,
      heartbeat: null,
      receivedTime: null
    };
  },
  computed: {
    messagesString() {
      let res = "";
      this.messages.forEach(msg => {
        res += msg;
      });
      return res;
    },
    displayedItems() {
      if (!this.params) return [];

      return (
        Object.keys(this.params)
          .map(k => this.params[k])
          .filter(p => !this.search || p.label.toLowerCase().indexOf(this.search.toLowerCase()) >= 0)
          // Sort by type and then label
          .sort((a, b) => {
            const firstLevel = a.type.localeCompare(b.type);
            if (firstLevel !== 0) return firstLevel;

            // Only sort by label if the types are the same (firstLevel === 0)
            return a.label.localeCompare(b.label);
          })
      );
    }
  },
  async created() {
    // fetch the data when the view is created and the data is
    // already being observed
    await this.fetchConnection(this.$route.params.serial);
    this.connect(this.$route.params.serial);
  },
  watch: {
    // call again the method if the route changes
    $route($route) {
      this.connect($route.params.serial);
    }
  },
  beforeDestroy() {
    this.ws.close();
    clearInterval(this.heartbeat);
  },
  methods: {
    async fetchConnection(id) {
      try {
        const d = await CoreAPI.getConnection(id);
        this.loading = false;
        this.connection = d;
        this.location = d.location ? d.location : {};
      } catch (error) {
        this.loading = false;
        this.connection = {};
        this.location = {};

        if (error.isNetworkError) {
          await modals.showLostConnectionModal(this.$bvModal);
        } else {
          this.error = error;
        }
      }
    },
    extractValues(data) {
      const newData = Object.assign({}, this.params);
      Object.keys(data[data.class]).forEach(identifier => {
        if (data.class === "scalar") {
          data[data.class][identifier].data.split(",").forEach(el => {
            if (!newData[identifier]) {
              newData[identifier] = {
                type: data.class,
                label: identifier,
                units: data[data.class][identifier].units,
                value: new Array(500).fill(0)
              };
            }
            newData[identifier].value.push(parseInt(el, 10));
            if (newData[identifier].value.length > 500) {
              newData[identifier].value.shift();
            }
          });
        } else {
          newData[identifier] = {
            type: data.class,
            label: identifier,
            value: data[data.class][identifier].data,
            units: data[data.class][identifier].units
          };
        }
      });
      this.params = newData;
    },
    connect(id) {
      if (this.ws) {
        this.ws.close();
      }
      this.ws = CoreAPI.getStream(id);
      this.messages = [];
      let settingsExtracted = false;

      this.ws.onmessage = event => {
        const data = JSON.parse(event.data);
        this.receivedTime = new Date();

        // Load the settings from the current message if they have been provided and not previously loaded
        if (!settingsExtracted && data.setting) {
          this.extractValues({
            class: "setting",
            setting: data.setting
          });
          settingsExtracted = true;
        }
        this.extractValues(data);
        this.messages.push(event.data);
        this.location = !data.location ? {} : data.location;
        this.patient = !data.patientID ? {} : { ID: data.patientID };
        if (this.messages.length > 10) {
          this.messages.shift();
        }
      };
      this.ws.onopen = () => {
        this.heartbeat = setInterval(() => {
          try {
            this.ws.send("ping");
          } catch (e) {
            clearInterval(this.heartbeat);
          }
        }, 5000);
      };
    }
  }
};
</script>
